import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { DateState } from '@Components/DateRangePicker';
import GroupsLayout from '@Components/GroupsLayout';
import GroupsTableProps from '@Components/GroupsTable/GroupsTable.types';
import { MenuItem } from '@Components/PopOverMenu';
import Table, { TableHead } from '@Components/Table';
import { CommonConstants } from '@Constants/index';
import { HeaderCheckboxState, Icon } from '@Typings/global.types';
import {
  getDateFromISODateTimeString,
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

const GroupsTable: FunctionComponent<GroupsTableProps> = (
  props: GroupsTableProps
): JSX.Element => {
  const { tableProps, isAdmin, handleBulk, ...resProps } = props;
  const {
    loading,
    rows,
    paginationProps,
    setChecked,
    checked,
    ...resTableProps
  } = tableProps;
  const { count, ...resPaginationProps } = paginationProps;

  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [headerCheckboxState, setHeaderCheckboxState] =
    useState<HeaderCheckboxState>({ checked: false, indeterminate: false });

  const nonCheckableRows = useMemo(
    () => rows.filter((row) => !row.isNotCheckable),
    [rows]
  );

  const syncState = useCallback(() => {
    const nameParam = query.get(CommonConstants.QUERY_PARAMS.COMMON.NAME) || '';
    const dateStartParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_START) as string) ||
        ''
    );
    const dateEndParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_END) as string) || ''
    );
    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    setPage(pageNo);
    setRowsPerPage(pageSize);
    setSearchValue(`${nameParam}`);
    setDateState((prevState) => [
      {
        ...prevState[0],
        startDate: dateStartParam,
        endDate: dateEndParam,
      },
    ]);
  }, [setPage, setRowsPerPage]);

  const clearSelection = (): void => {
    if (handleBulk) {
      handleBulk(0);
    }
    if (setChecked) {
      setChecked([]);
    }
  };

  const onSearch = (value: string) => {
    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.NAME]: value,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });
    changeRoute(searchParam);
    clearSelection();
  };

  const getDateState = (startDate?: Date, endDate?: Date) => {
    const dateParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });

    changeRoute(dateParams);
    clearSelection();
  };

  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.ORDER
    ) as string;
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]: order,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    return [
      {
        label: t('common.messages.Group Name'),
        icon: getTableHeadIcon(CommonConstants.QUERY_PARAMS.COMMON.NAME),
        width: 28.4,
        onClick: () =>
          handleTableHeadClick(CommonConstants.QUERY_PARAMS.COMMON.NAME),
      },
      {
        label: t('common.table.Public'),
        width: 17,
      },
      {
        label: t('common.table.Created Date'),
        icon: getTableHeadIcon(CommonConstants.INITIAL_SORT_ORDER),
        width: 23.8,
        align: 'right',
        onClick: () => handleTableHeadClick(CommonConstants.INITIAL_SORT_ORDER),
      },
      {
        label: t('common.table.Actions'),
        width: 8.5,
        align: 'center',
      },
    ];
  };

  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    count
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(pageParam);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(pageParams);
  };

  const selectPage = (): void => {
    clearSelection();
    setHeaderCheckboxState({ checked: true, indeterminate: false });
    if (setChecked) {
      setChecked(nonCheckableRows.map((row) => row.id));
    }
  };

  const select1000 = () => {
    clearSelection();
    if (handleBulk) {
      handleBulk(Math.min(count, CommonConstants.BULK_COUNT));
    }
  };

  const menuOptions: MenuItem[] = [
    {
      label: t('common.table.Select Page'),
      value: 'page',
      onClick: selectPage,
    },
    {
      label: t('common.table.Select first 1000'),
      value: '1000',
      onClick: select1000,
    },
    {
      label: t('common.table.Clear Selection'),
      value: 'clear',
      onClick: clearSelection,
    },
  ];

  const headerCheckClickHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked && !headerCheckboxState.indeterminate) {
      selectPage();
    } else {
      clearSelection();
    }
  };

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  useEffect(() => {
    if (!loading) {
      if (checked?.length && checked.length !== nonCheckableRows.length) {
        setHeaderCheckboxState({ checked: false, indeterminate: true });
      } else if (
        checked?.length &&
        checked.length === nonCheckableRows.length
      ) {
        setHeaderCheckboxState({ checked: true, indeterminate: false });
      } else {
        setHeaderCheckboxState({ checked: false, indeterminate: false });
      }
    }
  }, [checked?.length, nonCheckableRows.length, loading]);

  const TableComponent = (
    <Table
      loading={loading}
      rows={rows}
      tableHead={getTableHead()}
      checked={checked}
      setChecked={setChecked}
      checkboxMenuProps={{
        menuOptions: !isAdmin ? menuOptions : undefined,
        checkboxProps: {
          checked: headerCheckboxState.checked,
          indeterminate: headerCheckboxState.indeterminate,
          onChange: headerCheckClickHandler,
          disabled: !nonCheckableRows.length,
        },
      }}
      paginationProps={{
        count,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...resPaginationProps,
      }}
      {...resTableProps}
    />
  );

  return (
    <GroupsLayout
      isAdmin={isAdmin}
      showFilterControls={false}
      searchProps={{
        value: searchValue,
        setValue: setSearchValue,
        placeholder: t('common.tableFilters.Search by name'),
        onSearch,
      }}
      cancelBtnProps={{ onClick: clearSelection, disabled: loading }}
      selectedBtnProps={{ onClick: clearSelection, disabled: loading }}
      dateRangeProps={{
        dateState,
        setDateState,
        getDateState,
      }}
      table={TableComponent}
      {...resProps}
    />
  );
};

export default GroupsTable;
